"use client";

import { Check, ChevronDown, Search } from "lucide-react";
import Image from "next/image";
import { useMemo, useState } from "react";
import {
  defaultFacelessMotionClipDurationSeconds,
  facelessMotionVideoModelOptions,
  getFacelessMotionVideoCreditsPerScene,
  pickCoveringFacelessMotionDurationSeconds,
  type FacelessMotionVideoModelId,
  type FacelessMotionVideoModelOption,
  type FacelessMotionVideoResolution,
} from "@reelmagic/domain";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CreditMark } from "@/components/icons/CreditMark";
import { cn } from "@/lib/utils";

type ModelFamily =
  | "google"
  | "pixverse"
  | "vidu"
  | "alibaba"
  | "grok"
  | "kling"
  | "runway"
  | "seedance";

const familyDetails: Record<
  ModelFamily,
  { iconSrc: string; label: string }
> = {
  google: { iconSrc: "/ai-models/veo.svg", label: "Google" },
  pixverse: { iconSrc: "/ai-models/pixverse.svg", label: "PixVerse" },
  vidu: { iconSrc: "/ai-models/vidu.svg", label: "Vidu" },
  alibaba: {
    iconSrc: "/ai-models/wan.svg",
    label: "Alibaba",
  },
  grok: {
    iconSrc: "/ai-models/grok.svg",
    label: "Grok",
  },
  kling: {
    iconSrc: "/ai-models/kling.svg",
    label: "Kling",
  },
  runway: {
    iconSrc: "/ai-models/runway.svg",
    label: "Runway",
  },
  seedance: {
    iconSrc: "/ai-models/seedance.svg",
    label: "Seedance",
  },
};

const familyOrder: readonly ModelFamily[] = [
  "seedance",
  "grok",
  "runway",
  "kling",
  "alibaba",
  "pixverse",
  "vidu",
  "google",
];

function getModelFamily(id: FacelessMotionVideoModelId): ModelFamily {
  if (id.startsWith("google/")) return "google";
  if (id.startsWith("pixverse/")) return "pixverse";
  if (id.startsWith("vidu/")) return "vidu";
  if (id.startsWith("x-ai/")) return "grok";
  if (id.startsWith("kwaivgi/")) return "kling";
  if (id.startsWith("bytedance/")) return "seedance";
  if (id.startsWith("runway/")) return "runway";
  return "alibaba";
}

function formatCredits(option: FacelessMotionVideoModelOption, resolution: FacelessMotionVideoResolution) {
  const credits = getCreditsPerSecond(option, resolution);
  return credits === null ? null : `${Number(credits.toFixed(1))} credits/sec`;
}

function getCreditsPerSecond(
  option: FacelessMotionVideoModelOption,
  resolution: FacelessMotionVideoResolution,
) {
  const pricingResolution = option.supportedResolutions.includes(resolution)
    ? resolution
    : option.supportedResolutions[0];
  if (!pricingResolution) return null;
  const durationSeconds = pickCoveringFacelessMotionDurationSeconds(
    defaultFacelessMotionClipDurationSeconds,
    option.supportedDurations,
  );
  return getFacelessMotionVideoCreditsPerScene({ modelId: option.id, resolution: pricingResolution, durationSeconds }) / durationSeconds;
}

function ModelIcon({ family, className }: { family: ModelFamily; className?: string }) {
  const details = familyDetails[family];

  return (
    <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-white/[0.07] text-[var(--rm-text-soft)]", className)}>
      <Image
        alt=""
        aria-hidden="true"
        className="size-5 brightness-0 invert"
        height={20}
        src={details.iconSrc}
        width={20}
      />
    </span>
  );
}

function CreditPrice({
  className,
  label,
}: {
  className?: string;
  label: string | null;
}) {
  if (!label) return null;

  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap", className)}>
      <CreditMark className="size-[0.95em] shrink-0" />
      <span>{label}</span>
    </span>
  );
}

type VideoModelPickerProps = {
  disabled?: boolean;
  onValueChange: (value: string) => void;
  resolution: FacelessMotionVideoResolution;
  value: FacelessMotionVideoModelId;
};

const hiddenModelIds = new Set<FacelessMotionVideoModelId>([
  "kwaivgi/kling-video-o1",
  "alibaba/happyhorse-1.0",
  "alibaba/happyhorse-1.1",
  "alibaba/wan-2.6",
]);

const klingModelOrder: readonly FacelessMotionVideoModelId[] = [
  "kwaivgi/kling-v3.0-omni",
  "kwaivgi/kling-v3.0-pro",
  "kwaivgi/kling-v3.0-turbo",
  "kwaivgi/kling-v3.0-std",
];

export function VideoModelPicker({
  disabled = false,
  onValueChange,
  resolution,
  value,
}: VideoModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = facelessMotionVideoModelOptions.find((option) => option.id === value);
  const selectedFamily = getModelFamily(value);
  const groupedModels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return familyOrder.map((family) => ({
      family,
      models: facelessMotionVideoModelOptions
        .filter((option) =>
          !hiddenModelIds.has(option.id) &&
          getModelFamily(option.id) === family &&
          (!normalizedQuery || `${option.label} ${option.description} ${familyDetails[family].label}`.toLowerCase().includes(normalizedQuery)),
        )
        .slice()
        .sort(
          (left, right) =>
            family === "kling"
              ? klingModelOrder.indexOf(left.id) - klingModelOrder.indexOf(right.id)
              : (getCreditsPerSecond(right, resolution) ?? -1) -
                (getCreditsPerSecond(left, resolution) ?? -1),
        ),
    })).filter((group) => group.models.length > 0);
  }, [query, resolution]);

  return (
    <Popover open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          aria-expanded={open}
          aria-haspopup="listbox"
          className="group flex min-h-14 w-full items-center gap-3 rounded-2xl border border-border bg-white/[0.035] px-3 py-2 text-left shadow-sm outline-none transition duration-200 hover:border-white/20 hover:bg-white/[0.055] active:scale-[0.99] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/35"
          disabled={disabled}
          type="button"
        >
          <ModelIcon family={selectedFamily} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground">{selected?.label ?? "Choose video model"}</span>
            {selected ? (
              <CreditPrice
                className="text-xs text-[var(--rm-text-muted)]"
                label={formatCredits(selected, resolution)}
              />
            ) : (
              <span className="block truncate text-xs text-[var(--rm-text-muted)]">
                Select a model
              </span>
            )}
          </span>
          <ChevronDown aria-hidden="true" className={cn("size-4 shrink-0 text-[var(--rm-text-muted)] transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(430px,calc(100vw-32px))] overflow-hidden rounded-[20px] border-white/10 bg-popover p-0 shadow-2xl shadow-black/35"
        sideOffset={8}
      >
        <div className="relative border-b border-white/[0.07]">
          <Search aria-hidden="true" className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[var(--rm-text-muted)]" />
          <input
            autoFocus
            className="h-12 w-full bg-transparent pl-11 pr-4 text-sm outline-none placeholder:text-[var(--rm-text-muted)]"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search video models..."
            value={query}
          />
        </div>
        <div
          aria-label="Video models"
          className="max-h-[min(420px,calc(100dvh-220px))] overscroll-contain overflow-y-auto p-2 touch-pan-y"
          onTouchMoveCapture={(event) => event.stopPropagation()}
          onWheelCapture={(event) => event.stopPropagation()}
          role="listbox"
        >
          {groupedModels.length ? groupedModels.map(({ family, models }) => (
            <section className="py-1" key={family}>
              <div className="flex items-center gap-2 px-2 pb-1 pt-2">
                <span className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[var(--rm-text-muted)]">{familyDetails[family].label}</span>
              </div>
              {models.map((option) => {
                const isSelected = option.id === value;
                return (
                  <button
                    aria-selected={isSelected}
                    className={cn("flex w-full items-center gap-3 rounded-[14px] px-2.5 py-2.5 text-left outline-none transition duration-150 hover:bg-white/[0.065] focus-visible:bg-white/[0.065] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40", isSelected && "bg-white/[0.075]")}
                    key={option.id}
                    onClick={() => { onValueChange(option.id); setOpen(false); setQuery(""); }}
                    role="option"
                    type="button"
                  >
                    <ModelIcon family={family} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold">{option.label}</span>
                      </span>
                      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-[var(--rm-text-muted)]">
                        <CreditPrice label={formatCredits(option, resolution)} />
                        <span aria-hidden="true">·</span>
                        <span className="truncate">
                          {option.supportedResolutions.join(" / ")}
                        </span>
                      </span>
                    </span>
                    <Check aria-hidden="true" className={cn("size-4 shrink-0 text-primary transition-opacity", isSelected ? "opacity-100" : "opacity-0")} />
                  </button>
                );
              })}
            </section>
          )) : <p className="px-4 py-10 text-center text-sm text-[var(--rm-text-muted)]">No models match “{query}”.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
